import classNames from "classnames";
import AppNotifier from "components/v2/AppNotifier";
import ErrorComponent from "components/v2/ErrorFallbackComponent/ErrorComponent";
import UnpaidInvoiceBanner from "components/v2/UnpaidInvoiceBanner/Index";
import WebSocketIllustoTemplate from "components/v2/WebSocket/WebSocketIllustoTemplate";
import useEntityIdSync from "hooks/useEntityIdSync";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { connect } from "react-redux";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { getDatafeedOrganizationConfig } from "store/dataFeed/actions";
import { selectSelectedEntityId, selectSelectedEntityRole } from "store/entity/selectors";
import {
  selectAppAdditionalPermissionsAccountRestrictionBySelectedUserId,
  selectAppAdditionalPermissionsUnpaidInvoiceBannerBySelectedUserId,
} from "store/entityConfig/selectors";
import { getWorkspaceDetailsOfOrganization } from "store/workspace/action";
import { setUserProperties } from "utils";

import AuthenticatedErrorFallbackComponent from "../../../../components/v2/ErrorFallbackComponent/AuthenticatedErrorFallbackComponent";
import FullScreenLoader from "../../../../components/v2/Loaders/FullScreenLoader";
import Sidebar from "../../../../components/v2/Sidebar";
import commonStyles from "../../../../components/v2/style.module.css";
import WebSocketZipURL from "../../../../components/v2/WebSocket/WebSocketZipURL";
import { addRenderingErrorToDataDogRum } from "../../../../externals/datadog";
import { getEntityConfig } from "../../../../store/entityConfig/actions";
import { getOrganizationDetails } from "../../../../store/organization/action";
import { getRoleDetails } from "../../../../store/role/actions";
import { getMyProfile } from "../../../../store/user/actions";
import { isUserAuthenticated } from "../../../../utils/Storage";
import { logoutUser } from "../../utils";
import styles from "./style.module.css";

const PrivateRoute = ({
  getMyProfile,
  getEntityConfig,
  getRoleDetails,
  entityId,
  entityRole,
  getOrganizationDetails,
  getWorkspaceDetailsOfOrganization,
  isUnpaidInvoiceBannerEnabled,
  getDatafeedOrganizationConfig,
  isAccountRestrictionEnabled,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isAuthenticated = isUserAuthenticated();
  const [isLoadingGetMyProfile, setIsLoadingGetMyProfile] = useState(true);
  const [isLoadingGetUserSettings, setIsLoadingGetUserSettings] = useState(false);
  const [isGetMyProfileError, setIsGetMyProfileError] = useState(false);

  const fetchMyProfile = useCallback(async () => {
    setIsLoadingGetMyProfile(true);
    try {
      if (isAuthenticated) {
        const response = await getMyProfile({});

        const payload = response?.payload;
        const error = response?.error;

        // logout the user only if the error is 401
        if (typeof error?.message === "string" && error?.message?.includes("401")) {
          logoutUser(navigate);

          return;
        }

        if (payload && payload?.status && payload?.data) {
          const { data } = payload;

          setUserProperties(data);
        } else {
          setIsGetMyProfileError(true);
          console.info("Payload is undefined or data is missing");

          return;
        }
      }
    } catch (error) {
      setIsGetMyProfileError(true);
      console.error(error);
    }
    setIsLoadingGetMyProfile(false);
  }, [isAuthenticated, getMyProfile, navigate]);

  useEffect(() => {
    fetchMyProfile();
  }, [fetchMyProfile]);

  const fetchDatafeedOrganizationConfig = useCallback(
    async (entityId) => {
      try {
        await getDatafeedOrganizationConfig({ entityId });
      } catch (error) {
        console.error(error);
      }
    },
    [getDatafeedOrganizationConfig],
  );

  const fetchUserSettings = useCallback(async () => {
    setIsLoadingGetUserSettings(true);
    try {
      if (entityId && entityRole) {
        await Promise.all([
          ...(entityId ? [getEntityConfig({ entityId })] : []),
          ...(entityRole ? [getRoleDetails({ roleName: entityRole })] : []),
          ...(entityId ? [fetchDatafeedOrganizationConfig(entityId)] : []),
        ]);
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoadingGetUserSettings(false);
  }, [entityId, entityRole, getEntityConfig, getRoleDetails, fetchDatafeedOrganizationConfig]);

  useEffect(() => {
    fetchUserSettings();
  }, [fetchUserSettings]);

  const fetchOrganization = useCallback(async () => {
    try {
      if (entityId) {
        await getOrganizationDetails({
          organizationIds: [entityId],
          includeWorkspaces: true,
        });
      }
    } catch (error) {
      console.log(error);
    }
  }, [getOrganizationDetails, entityId]);

  const fetchWorkspace = useCallback(async () => {
    try {
      if (entityId) {
        await getWorkspaceDetailsOfOrganization({
          organizationIds: [entityId],
          includeWorkspaces: true,
        });
      }
    } catch (error) {
      console.log(error);
    }
  }, [getWorkspaceDetailsOfOrganization, entityId]);

  useEffect(() => {
    fetchOrganization();
    fetchWorkspace();
  }, [fetchOrganization, fetchWorkspace]);

  useEntityIdSync({ entityId: id, isProfileLoaded: !isLoadingGetMyProfile, entityRole });

  const isSidebarHidden = useMemo(() => {
    return pathname.startsWith("/studio/publish");
  }, [pathname]);

  useEffect(() => {
    if (isAccountRestrictionEnabled && pathname !== "/dashboard") {
      navigate("/dashboard", { replace: true });
    }
  }, [isAccountRestrictionEnabled, pathname, navigate]);

  if (isGetMyProfileError || isAccountRestrictionEnabled) {
    return (
      <ErrorComponent
        title={isAccountRestrictionEnabled ? "Account Blocked" : "Unable to Load Your Profile"}
        subtitle={
          isAccountRestrictionEnabled ? (
            <div className={styles.blockedAccountMessage}>
              <p>
                Your Account has been suspended due to outstanding dues. To unlock your account, please clear your dues.
              </p>
              <p>
                For further information contact your account manager or send an email to{" "}
                <a href="mailto:cs@magnifi.ai" className={styles.emailLink}>
                  cs@magnifi.ai
                </a>
                .
              </p>
            </div>
          ) : (
            "We're experiencing technical difficulties. Please try refreshing the page or contact support if the issue persists."
          )
        }
        isAuthenticated={!isAccountRestrictionEnabled && isAuthenticated}
      />
    );
  }

  if (isAuthenticated) {
    return (
      <ErrorBoundary FallbackComponent={AuthenticatedErrorFallbackComponent} onError={addRenderingErrorToDataDogRum}>
        <WebSocketZipURL />
        <WebSocketZipURL isForClipVideo={true} />
        <WebSocketIllustoTemplate />
        {isLoadingGetMyProfile || isLoadingGetUserSettings ? (
          <FullScreenLoader />
        ) : (
          <div className={styles.container}>
            {!isSidebarHidden ? <Sidebar /> : null}
            <div className={styles.mainWrapper}>
              {isUnpaidInvoiceBannerEnabled ? <UnpaidInvoiceBanner /> : null}
              <div className={styles.mainWrapper}>
                <div className={classNames(styles.contentContainer, commonStyles.scrollBar)}>
                  <Outlet />
                  <AppNotifier />
                </div>
              </div>
            </div>
          </div>
        )}
      </ErrorBoundary>
    );
  }

  return <Navigate to="/login" />;
};

const mapStateToProps = createStructuredSelector({
  entityRole: selectSelectedEntityRole,
  entityId: selectSelectedEntityId,
  isUnpaidInvoiceBannerEnabled: selectAppAdditionalPermissionsUnpaidInvoiceBannerBySelectedUserId,
  isAccountRestrictionEnabled: selectAppAdditionalPermissionsAccountRestrictionBySelectedUserId,
});

const mapDispatchToProps = {
  getMyProfile,
  getEntityConfig,
  getRoleDetails,
  getOrganizationDetails,
  getWorkspaceDetailsOfOrganization,
  getDatafeedOrganizationConfig,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute);
